import React, { useEffect, useRef, useState } from "react";
import { Box, Grid, Skeleton } from "@mui/material";
import api from "../../api/client";
import { endpoints } from "../../api/endpoints";
import { LeaveTypeDistributionCards, MonthlyLeaveSummaryCards } from "./leaveAllocationUi";

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function paidDaysOf(row) {
  if (row?.paidDays != null && row.paidDays !== "") return Number(row.paidDays) || 0;
  return Number(row?.totalDays) || 0;
}

function usesMonthlyPool(code) {
  const n = String(code || "").toLowerCase();
  if (!n) return false;
  if (n.includes("comp") || n === "lop" || n.includes("loss of pay")) return false;
  if (n.includes("maternity") || n.includes("paternity") || n.includes("marriage") || n.includes("bereavement")) {
    return false;
  }
  return true;
}

function typeMatches(card, leaveType) {
  const t = String(leaveType || "").toLowerCase();
  const code = String(card?.code || "").toLowerCase();
  const name = String(card?.name || "").toLowerCase();
  if (!t) return false;
  return t === code || t === name || (code && t.includes(code)) || (name && t.includes(name));
}

function usedForType(usedByType, type) {
  const code = String(type.code || "").toLowerCase();
  const name = String(type.name || "").toLowerCase();
  let used = 0;
  for (const [key, days] of Object.entries(usedByType)) {
    const k = String(key).toLowerCase();
    if (k === code || k === name) used += Number(days) || 0;
  }
  return round2(used);
}

async function loadTypeBreakdown(monthlyLeave, compOffAvailable) {
  const [typesRes, leavesRes] = await Promise.all([
    api.get(endpoints.leave.types),
    api.get(endpoints.leave.getLeaveRequest),
  ]);
  const types = typesRes.data?.data || typesRes.data?.leaveTypes || (Array.isArray(typesRes.data) ? typesRes.data : []);
  const leaves = Array.isArray(leavesRes.data) ? leavesRes.data : leavesRes.data?.data || [];
  const year = monthlyLeave?.year || new Date().getFullYear();
  const usedByType = {};
  for (const row of leaves) {
    if (!["approved", "pending"].includes(row.status)) continue;
    if (new Date(row.startDate).getFullYear() !== year) continue;
    const key = String(row.leaveType || "");
    if (!key) continue;
    usedByType[key] = round2((usedByType[key] || 0) + paidDaysOf(row));
  }
  return (Array.isArray(types) ? types : [])
    .filter((t) => t && t.active !== false)
    .map((t) => {
      const code = t.code || t.name;
      const isComp = Boolean(t.isCompOff) || /comp/i.test(String(code));
      const isLop = String(code || "").toLowerCase() === "lop" || /loss of pay/i.test(String(t.name || ""));
      const pool = usesMonthlyPool(code);
      return {
        code,
        name: t.name || code,
        used: usedForType(usedByType, t),
        remaining: isComp ? Number(compOffAvailable) || 0 : isLop ? 0 : pool ? Number(monthlyLeave?.remaining) || 0 : 0,
        remainingHint: pool ? "shared monthly pool remaining" : isComp ? "comp-off available" : "remaining",
        pool,
        isCompOff: isComp,
        isLop,
      };
    });
}

export default function LeaveBalanceCards({ sx, refreshKey = 0, lastApply = null }) {
  const [monthlyLeave, setMonthlyLeave] = useState(null);
  const [compOffAvailable, setCompOffAvailable] = useState(0);
  const [lopDaysTaken, setLopDaysTaken] = useState(0);
  const [lopYearToDate, setLopYearToDate] = useState(0);
  const [leaveTypeBreakdown, setLeaveTypeBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!lastApply?.token) return;
    const days = round2(lastApply.days);
    const paid = round2(lastApply.paidDays != null ? lastApply.paidDays : days);
    if (lastApply.monthlyLeave) setMonthlyLeave(lastApply.monthlyLeave);
    else {
      setMonthlyLeave((prev) => {
        if (!prev || lastApply.leaveType && /comp|lop|loss of pay/i.test(String(lastApply.leaveType))) return prev;
        return {
          ...prev,
          taken: round2((Number(prev.taken) || 0) + paid),
          remaining: round2(Math.max(0, (Number(prev.remaining) || 0) - paid)),
        };
      });
    }
    if (lastApply.compOffAvailable != null) setCompOffAvailable(Number(lastApply.compOffAvailable) || 0);
    if (lastApply.lopDaysTaken != null) setLopDaysTaken(Number(lastApply.lopDaysTaken) || 0);
    if (lastApply.lopYearToDate != null) setLopYearToDate(Number(lastApply.lopYearToDate) || 0);
    setLeaveTypeBreakdown((prev) =>
      prev.map((t) => {
        if (!typeMatches(t, lastApply.leaveType)) return t;
        const next = { ...t, used: round2((Number(t.used) || 0) + days) };
        if (t.isCompOff) next.remaining = round2(Math.max(0, (Number(t.remaining) || 0) - days));
        if (t.pool && lastApply.monthlyLeave?.remaining != null) next.remaining = lastApply.monthlyLeave.remaining;
        return next;
      })
    );
  }, [lastApply]);

  useEffect(() => {
    let cancelled = false;
    if (!hasLoadedRef.current) setLoading(true);
    (async () => {
      try {
        const res = await api.get(endpoints.employees.remainingLeaves);
        if (cancelled) return;
        const monthly = res.data?.monthlyLeave || null;
        const compOff = Number(res.data?.compOffAvailable) || 0;
        setMonthlyLeave(monthly);
        setCompOffAvailable(compOff);
        setLopDaysTaken(Number(res.data?.lopDaysTaken) || 0);
        setLopYearToDate(Number(res.data?.lopYearToDate) || 0);
        const breakdown = await loadTypeBreakdown(monthly, compOff);
        if (!cancelled) {
          setLeaveTypeBreakdown(breakdown);
          hasLoadedRef.current = true;
        }
      } catch (_) {
        if (!cancelled && !hasLoadedRef.current) {
          setMonthlyLeave(null);
          setLeaveTypeBreakdown([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  if (loading) {
    return (
      <Grid container spacing={2} sx={{ mb: 2.5, ...sx }}>
        {[1, 2, 3, 4, 5].map((k) => (
          <Grid item xs={6} sm={4} md={2.4} key={k}>
            <Skeleton variant="rounded" height={88} sx={{ borderRadius: 2.5 }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Box sx={sx}>
      <MonthlyLeaveSummaryCards
        monthlyLeave={monthlyLeave}
        lopDaysTaken={lopDaysTaken}
        lopYearToDate={lopYearToDate}
        compOffAvailable={compOffAvailable}
      />
      <LeaveTypeDistributionCards types={leaveTypeBreakdown} />
    </Box>
  );
}
